/**
 * Dialog for loading templates with list, search, and actions
 */

import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from "primereact/confirmdialog";
import { Tooltip } from "primereact/tooltip";
import PropTypes from "prop-types";
import ModalBase from "../../components/Modals/ModalBase";
import PromptDialog from "./PromptDialog";
import "./DesktopManager.css";

const LoadTemplateDialog = ({
  visible,
  onHide,
  onLoad,
  onDuplicate,
  onRename,
  onDelete,
  onRefresh,
}) => {
  const [templates, setTemplates] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Prompt dialog state
  const [promptVisible, setPromptVisible] = useState(false);
  const [promptConfig, setPromptConfig] = useState({
    title: "",
    message: "",
    defaultValue: "",
    placeholder: "",
    onConfirm: null,
  });

  // Load templates when dialog opens
  useEffect(() => {
    if (visible) {
      loadTemplates();
    }
  }, [visible]);

  const loadTemplates = async () => {
    setLoading(true);
    setError("");

    try {
      const list = await onRefresh();
      setTemplates(list || []);
    } catch (err) {
      console.error("Error loading templates:", err);
      setError(err.message || "Falha ao carregar lista de templates");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = async (template) => {
    try {
      await onLoad(template.id);
      onHide(); // Close dialog after successful load
    } catch (err) {
      console.error("Error loading template:", err);
      setError(err.message || "Falha ao carregar template");
    }
  };

  const handleDuplicate = (template) => {
    // Generate unique name suggestion
    let suggestedName = `${template.name} (Cópia)`;
    let counter = 1;

    // Check if name exists and add counter if needed
    while (templates.some((t) => t.name === suggestedName)) {
      counter++;
      suggestedName = `${template.name} (Cópia ${counter})`;
    }

    // Show prompt with suggested name
    setPromptConfig({
      title: "Duplicar Template",
      message: `Digite o nome para a duplicata de "${template.name}":`,
      defaultValue: suggestedName,
      placeholder: "Nome do template",
      onConfirm: async (newName) => {
        // Check if name exists
        const existing = templates.find(
          (t) => t.name.toLowerCase() === newName.toLowerCase(),
        );
        if (existing) {
          confirmDialog({
            message: `Já existe um template com o nome "${newName}". Deseja sobrescrever?`,
            header: "Confirmar Sobrescrita",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-warning",
            acceptLabel: "Sobrescrever",
            rejectLabel: "Cancelar",
            accept: async () => {
              try {
                // Delete existing first, then duplicate with the name
                await onDelete(existing.id);
                await onDuplicate(template.id, newName);
                await loadTemplates();
                setPromptVisible(false);
              } catch (err) {
                console.error("Error duplicating template:", err);
                setError(err.message || "Falha ao duplicar template");
              }
            },
          });
        } else {
          try {
            await onDuplicate(template.id, newName);
            await loadTemplates();
            setPromptVisible(false);
          } catch (err) {
            console.error("Error duplicating template:", err);
            setError(err.message || "Falha ao duplicar template");
          }
        }
      },
    });
    setPromptVisible(true);
  };

  const handleRename = (template) => {
    setPromptConfig({
      title: "Renomear Template",
      message: `Digite o novo nome para "${template.name}":`,
      defaultValue: template.name,
      placeholder: "Nome do template",
      onConfirm: async (newName) => {
        // Check if name didn't change
        if (newName === template.name) {
          setPromptVisible(false);
          return;
        }

        // Check if name exists
        const existing = templates.find(
          (t) =>
            t.name.toLowerCase() === newName.toLowerCase() &&
            t.id !== template.id,
        );

        if (existing) {
          confirmDialog({
            message: `Já existe um template com o nome "${newName}". Deseja sobrescrever?`,
            header: "Confirmar Sobrescrita",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-warning",
            acceptLabel: "Sobrescrever",
            rejectLabel: "Cancelar",
            accept: async () => {
              try {
                // Delete existing first, then rename
                await onDelete(existing.id);
                await onRename(template.id, newName);
                await loadTemplates();
                setPromptVisible(false);
              } catch (err) {
                console.error("Error renaming template:", err);
                setError(err.message || "Falha ao renomear template");
              }
            },
          });
        } else {
          try {
            await onRename(template.id, newName);
            await loadTemplates();
            setPromptVisible(false);
          } catch (err) {
            console.error("Error renaming template:", err);
            setError(err.message || "Falha ao renomear template");
          }
        }
      },
    });
    setPromptVisible(true);
  };

  const handleDelete = (template) => {
    confirmDialog({
      message: `Tem certeza que deseja excluir o template "${template.name}"? Esta ação não pode ser desfeita.`,
      header: "Excluir Template",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Excluir",
      rejectLabel: "Cancelar",
      accept: async () => {
        try {
          await onDelete(template.id);
          await loadTemplates(); // Refresh list
        } catch (err) {
          console.error("Error deleting template:", err);
          setError(err.message || "Falha ao excluir template");
        }
      },
    });
  };

  const formatDate = (timestamp) => {
    try {
      let date;
      if (typeof timestamp === "number" && timestamp < 4102444800000) {
        date = new Date(timestamp * 1000);
      } else {
        date = new Date(timestamp);
      }

      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  // Column templates
  const nameTemplate = (rowData) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.375rem",
          cursor: "pointer",
        }}
        onDoubleClick={() => handleLoad(rowData)}
      >
        <i
          className="pi pi-palette"
          style={{ fontSize: "12px", color: "var(--primary-color)" }}
        ></i>
        <span
          style={{
            fontSize: "12px",
            fontWeight: "500",
            color: "var(--text-color)",
          }}
        >
          {rowData.name}
        </span>
      </div>
    );
  };

  const dateTemplate = (rowData) => {
    return (
      <span
        style={{
          fontSize: "11px",
          color: "var(--text-color-secondary)",
        }}
      >
        {formatDate(rowData.timestamp)}
      </span>
    );
  };

  const actionsTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", gap: "1px", justifyContent: "center" }}>
        <Button
          icon="pi pi-download"
          className="p-button-text p-button-sm p-button-success template-action-btn-load"
          onClick={() => handleLoad(rowData)}
          data-pr-tooltip="Aplicar ao gráfico"
          data-pr-position="top"
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id={`loadtemplatedialog-btn-load-${rowData.id}`}
        />
        <Button
          icon="pi pi-copy"
          className="p-button-text p-button-sm template-action-btn-duplicate"
          onClick={() => handleDuplicate(rowData)}
          data-pr-tooltip="Duplicar"
          data-pr-position="top"
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id={`loadtemplatedialog-btn-duplicate-${rowData.id}`}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm template-action-btn-rename"
          onClick={() => handleRename(rowData)}
          data-pr-tooltip="Renomear"
          data-pr-position="top"
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id={`loadtemplatedialog-btn-rename-${rowData.id}`}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger template-action-btn-delete"
          onClick={() => handleDelete(rowData)}
          data-pr-tooltip="Excluir"
          data-pr-position="top"
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id={`loadtemplatedialog-btn-delete-${rowData.id}`}
        />
      </div>
    );
  };

  const headerWithSearch = (
    <div className="desktop-dialog-header-inline">
      <span className="desktop-dialog-title">Carregar Template</span>
      <div className="desktop-header-search">
        <i className="pi pi-search" />
        <input
          type="text"
          autoComplete="off"
          className="p-inputtext p-component"
          placeholder="Buscar..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          data-evo-id="loadtemplatedialog-input-filter"
        />
      </div>
      <Button
        icon="pi pi-refresh"
        className="p-button-text p-button-sm p-button-rounded template-refresh-btn"
        onClick={loadTemplates}
        disabled={loading}
        data-pr-tooltip="Atualizar lista"
        data-pr-position="top"
        style={{ width: "26px", height: "26px", padding: "0" }}
        data-evo-id="loadtemplatedialog-btn-refresh"
      />
    </div>
  );

  const emptyMessage = (
    <div className="desktop-empty-state">
      <i className="pi pi-inbox"></i>
      <p>
        {globalFilter
          ? "Nenhum template encontrado com esse nome"
          : "Nenhum template salvo encontrado"}
      </p>
    </div>
  );

  return (
    <>
      <Tooltip target=".template-action-btn-load" />
      <Tooltip target=".template-action-btn-duplicate" />
      <Tooltip target=".template-action-btn-rename" />
      <Tooltip target=".template-action-btn-delete" />
      <Tooltip target=".template-refresh-btn" />

      <ModalBase
        header={headerWithSearch}
        visible={visible}
        onHide={onHide}
        size="large"
        variant="data"
        footer={null}
        className="desktop-load-dialog-modal"
      >
        <div className="desktop-load-dialog">
          {/* Error Message */}
          {error && (
            <Message
              severity="error"
              text={error}
              className="desktop-error-message"
            />
          )}

          {/* Template Table */}
          {loading ? (
            <div className="desktop-loading-state">
              <ProgressSpinner style={{ width: "50px", height: "50px" }} />
              <p>Carregando templates...</p>
            </div>
          ) : (
            <DataTable
              value={templates}
              globalFilter={globalFilter}
              emptyMessage={emptyMessage}
              scrollable
              showGridlines
              stripedRows
              size="small"
              className="desktop-table"
            >
              <Column
                field="name"
                header="Nome"
                body={nameTemplate}
                sortable
                headerClassName="desktop-col-name"
                bodyClassName="desktop-col-name"
              />
              <Column
                field="timestamp"
                header="Data"
                body={dateTemplate}
                sortable
                style={{ width: "130px" }}
                headerClassName="desktop-col-date"
                bodyClassName="desktop-col-date"
              />
              <Column
                header="Ações"
                body={actionsTemplate}
                style={{ width: "120px", textAlign: "center" }}
                headerClassName="desktop-col-actions"
                bodyClassName="desktop-col-actions"
              />
            </DataTable>
          )}
        </div>
      </ModalBase>

      {/* Prompt Dialog for Rename/Duplicate */}
      <PromptDialog
        visible={promptVisible}
        onHide={() => setPromptVisible(false)}
        onConfirm={promptConfig.onConfirm}
        title={promptConfig.title}
        message={promptConfig.message}
        defaultValue={promptConfig.defaultValue}
        placeholder={promptConfig.placeholder}
      />
    </>
  );
};

LoadTemplateDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

export default LoadTemplateDialog;
